/**
 * OfficialDocumentsPanel — Amtliche Dokumente aus einem Mietverhältnis heraus
 * erzeugen. 13 Standard-Vorlagen (Wohnungsgeberbestätigung, Mietbescheinigung,
 * Kautionsfreigabe, Mieterhöhung, ...) mit optionalem Deckblatt auf Geschäftspapier.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Download, Eye, Warning, PaperPlaneTilt, Envelope, Printer, Info, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function OfficialDocumentsPanel({ tenancyId, onGenerated, onAttachToEditor, preselectTemplateId }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState("");
  const [includeCover, setIncludeCover] = useState(true);
  // 2026-02 (feb) — Versendungszettel entfernt (Nutzeranforderung).
  const [saveToDocs, setSaveToDocs] = useState(true);
  // iter145 — Reines Formular (kein Geschäftspapier / kein Briefkopf)
  const [rawFormOnly, setRawFormOnly] = useState(false);
  // iter145 — Kündigungs-Zusatzfelder (nur bei Category=Kündigung)
  const [kExtra, setKExtra] = useState({
    grund: "", termin: "", eigenbedarf_person: "", eigenbedarf_verwandt: "",
    abmahnung_datum: "", rueckstand_eur: "", rueckstand_periode: "",
  });
  const [busy, setBusy] = useState(false);
  // 2026-09 · Send-Method-Modal state
  const [sendOpen, setSendOpen] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  // 2026-09 · Akkordeon-Zustand der Vorlagen-Kategorien (Meldewesen offen = Default)
  const [openCats, setOpenCats] = useState({});
  const toggleCat = (cat) => setOpenCats((s) => ({ ...s, [cat]: !(s[cat] ?? true) }));
  // iter167 · Anschreiben inline editierbar
  const [editCoverOpen, setEditCoverOpen] = useState(false);
  const [coverBody, setCoverBody] = useState("");
  const [coverBodyLoading, setCoverBodyLoading] = useState(false);
  const [coverEdited, setCoverEdited] = useState(false);
  // iter168 · Multi-Adress-Support: Eigentümer-Adresse aus Dropdown wählen
  const [ownerAddresses, setOwnerAddresses] = useState([]);
  const [ownerAddressId, setOwnerAddressId] = useState("");

  // Lade Owner-Adressen für das MV (via Preview-Context — API-Preview liefert ctx.owner.addresses)
  useEffect(() => {
    if (!selected) return;
    api.get(`/tenancies/${tenancyId}`).then(async ({ data: ten }) => {
      if (!ten?.property_id) return;
      const { data: prop } = await api.get(`/properties/${ten.property_id}`);
      if (!prop?.owner_contact_id) return;
      const { data: owner } = await api.get(`/contacts/${prop.owner_contact_id}`);
      const addrs = owner?.addresses || [];
      setOwnerAddresses(addrs);
      const primary = addrs.find((a) => a.is_primary) || addrs[0];
      if (primary) setOwnerAddressId(primary.id);
    }).catch(() => {});
  }, [tenancyId, selected?.id]);

  useEffect(() => {
    api.get("/official-documents/templates").then(({ data }) => setTemplates(data.templates || []));
  }, []);

  const grouped = templates.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  const isKuendigung = selected?.category === "Kündigung";

  const select = (t) => {
    setSelected(t);
    setSubject(t.default_subject || t.name);
    setCoverBody("");
    setCoverEdited(false);
  };

  useEffect(() => {
    if (!preselectTemplateId || !templates.length) return;
    const t = templates.find((x) => x.id === preselectTemplateId);
    if (t) select(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectTemplateId, templates]);

  // iter167 · Anschreiben-Editor öffnen — laden bei erstem Öffnen
  const openCoverEditor = async () => {
    if (!selected) return;
    setEditCoverOpen(true);
    if (coverBody || coverEdited) return;
    setCoverBodyLoading(true);
    try {
      const { data } = await api.post("/official-documents/cover-text", {
        template_id: selected.id,
        tenancy_id: tenancyId,
        extra_context: isKuendigung ? { kuendigung: kExtra } : undefined,
      });
      setCoverBody(data?.text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anschreiben konnte nicht geladen werden");
    } finally {
      setCoverBodyLoading(false);
    }
  };

  const previewCover = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.post("/official-documents/preview", {
        template_id: selected.id,
        tenancy_id: tenancyId,
        extra_context: isKuendigung ? { kuendigung: kExtra } : undefined,
        render_cover_only: true,
        cover_body_override: coverEdited ? coverBody : null,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 6000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const buildPayload = (overrides = {}) => ({
    template_id: selected.id,
    tenancy_id: tenancyId,
    subject: subject || null,
    include_cover: rawFormOnly ? false : includeCover,
    include_transmittal: false,
    save_to_docs: true,
    raw_form_only: rawFormOnly,
    extra_context: {
      ...(isKuendigung ? { kuendigung: kExtra } : {}),
      ...(ownerAddressId ? { owner_address_id: ownerAddressId } : {}),
    },
    // iter167 · Anschreiben-Override durchreichen wenn User editiert hat
    cover_body_override: coverEdited && coverBody ? coverBody : null,
    ...overrides,
  });

  const generate = async (mode /* "preview" | "download" | "attach" */) => {
    if (!selected) return;
    setBusy(true);
    try {
      // 2026-02 (feb) — Preview nutzt dedizierten /preview-Endpoint,
      // rendert NUR das Formular (kein Anschreiben, kein Sicherheitstoken).
      // 2026-10 — Vorschau wird NIE in die Dokumentenmappe abgelegt.
      // Ablage erfolgt erst beim tatsächlichen Versand (Direkt-Versand oder
      // Anhang im Editor / Download).
      let res;
      if (mode === "preview") {
        res = await api.post("/official-documents/preview", {
          template_id: selected.id,
          tenancy_id: tenancyId,
          extra_context: {
            ...(isKuendigung ? { kuendigung: kExtra } : {}),
            ...(ownerAddressId ? { owner_address_id: ownerAddressId } : {}),
          },
          save_to_docs: false,
        }, { responseType: "blob" });
      } else {
        const payload = buildPayload({ save_to_docs: saveToDocs });
        res = await api.post("/official-documents/generate", payload, {
          responseType: "blob",
        });
      }
      const docId = res.headers?.["x-document-id"] || res.headers?.get?.("X-Document-Id") || "";
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      if (mode === "preview") {
        window.open(url, "_blank");
        toast.success("Vorschau geöffnet");
      } else if (mode === "attach") {
        if (onAttachToEditor && docId) {
          onAttachToEditor({ document_id: docId, name: `${selected.name}.pdf`, filename: `${selected.id}.pdf` });
          toast.success("Formular als Anlage im Editor übernommen");
        } else {
          toast.info("Als Anlage: bitte öffne den Editor über das Mietverhältnis und wähle das eben erzeugte Dokument aus den Anhängen.");
        }
        if (onGenerated) onGenerated();
      } else if (mode === "print") {
        // 2026-09 · Post + Direkt drucken — öffne PDF in neuem Tab und starte Print-Dialog
        const win = window.open(url, "_blank");
        try {
          setTimeout(() => { win?.focus(); win?.print(); }, 900);
        } catch { /* browser may block auto-print */ }
        toast.success(saveToDocs ? "Druckansicht geöffnet · in Mappe abgelegt" : "Druckansicht geöffnet");
        if (onGenerated) onGenerated();
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selected.id}.pdf`;
        a.click();
        toast.success(saveToDocs ? "Dokument erzeugt & in Mappe abgelegt" : "Dokument erzeugt");
        if (onGenerated) onGenerated();
      }
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      // Axios blob-Fehler: response.data ist ein Blob → in Text konvertieren
      let msg = "Fehler beim Erzeugen";
      try {
        if (e?.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          msg = j.detail || j.message || msg;
        } else {
          msg = e?.response?.data?.detail || e?.message || msg;
        }
      } catch {
        msg = e?.message || msg;
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // 2026-09 · Direkt-Versand
  const directSend = async (method /* "email" | "post" | "fax" */) => {
    if (!selected) return;
    setSendBusy(true);
    try {
      const payload = {
        template_id: selected.id,
        tenancy_id: tenancyId,
        subject: subject || null,
        method,
        extra_context: {
          ...(isKuendigung ? { kuendigung: kExtra } : {}),
          ...(ownerAddressId ? { owner_address_id: ownerAddressId } : {}),
        },
        cover_body_override: coverEdited && coverBody ? coverBody : null,
      };
      const res = await api.post("/official-documents/direct-send", payload, {
        responseType: method === "post" ? "blob" : "json",
      });
      if (method === "post") {
        // Ergebnis ist die gemergte PDF (Cover + Formular) → in neuem Tab öffnen + auto-print
        const docId = res.headers?.["x-document-id"] || res.headers?.get?.("X-Document-Id") || "";
        const url = URL.createObjectURL(res.data);
        const win = window.open(url, "_blank");
        try { setTimeout(() => { win?.focus(); win?.print(); }, 900); } catch {}
        toast.success(docId ? "Druckansicht geöffnet · in Mappe abgelegt" : "Druckansicht geöffnet");
        setTimeout(() => URL.revokeObjectURL(url), 8000);
      } else if (method === "email") {
        toast.success(`E-Mail versendet: ${res.data?.message_id || "OK"}`);
      } else if (method === "fax") {
        toast.success(`Fax-Job angelegt: ${res.data?.job_id || "OK"}`);
      }
      if (onGenerated) onGenerated();
      setSendOpen(false);
    } catch (e) {
      let msg = "Direkt-Versand fehlgeschlagen";
      try {
        if (e?.response?.data instanceof Blob) {
          const j = JSON.parse(await e.response.data.text());
          msg = j.detail || msg;
        } else {
          msg = e?.response?.data?.detail || e?.message || msg;
        }
      } catch {}
      toast.error(msg);
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="official-docs-panel">
      {/* Vorlagen-Liste */}
      <div className="lg:col-span-1 space-y-3">
        <div className="flex items-start gap-2 bg-teal-50/60 border border-teal-200 rounded-md px-3 py-2 text-xs text-teal-900">
          <Info size={16} className="shrink-0 mt-0.5 text-teal-700" />
          <span>Alle Daten werden automatisch aus dem Mietverhältnis übernommen.</span>
        </div>
        <div className="space-y-2">
          {Object.entries(grouped).map(([cat, list]) => {
            const isOpen = openCats[cat] ?? true;
            return (
              <div key={cat} className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
                <button
                  type="button"
                  onClick={() => toggleCat(cat)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 border-b border-slate-200 transition"
                  data-testid={`official-cat-toggle-${cat}`}
                >
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-700 flex items-center gap-2">
                    {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    {cat}
                  </span>
                  <span className="text-[10px] text-slate-500 tabular-nums">{list.length}</span>
                </button>
                {isOpen && (
                  <ul>
                    {list.map((t) => (
                      <li key={t.id}>
                        <button
                          type="button"
                          onClick={() => select(t)}
                          className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition ${selected?.id === t.id ? "bg-teal-50 border-l-4 border-l-teal-600" : ""}`}
                          data-testid={`official-tpl-${t.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-slate-500" />
                            <span className="text-sm font-medium">{t.name}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 pl-6 line-clamp-2">{t.description}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Vorschau / Aktionen */}
      <div className="lg:col-span-2">
        {!selected ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-slate-500">
            <FileText size={32} className="mx-auto mb-3 text-slate-300" />
            <div className="text-sm font-medium text-slate-700">Wählen Sie links eine Vorlage aus.</div>
            <div className="text-xs mt-1 text-slate-500 max-w-md mx-auto">
              Öffnen Sie eine Kategorie, klicken Sie auf eine Vorlage — dann können Sie das Dokument
              in der Vorschau prüfen, direkt versenden oder als Anlage im Editor verwenden.
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid="official-config">
            <div>
              <h3 className="text-base font-semibold text-slate-900">{selected.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{selected.description}</p>
              <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
                {selected.category}
              </div>
            </div>

            <div>
              <Label className="text-xs">Betreff (Deckblatt / Versendungszettel)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="official-subject" />
            </div>

            {/* iter168 · Eigentümer-Adress-Auswahl bei mehreren Anschriften */}
            {ownerAddresses.length > 1 && (
              <div>
                <Label className="text-xs">Eigentümer-Anschrift</Label>
                <select
                  value={ownerAddressId}
                  onChange={(e) => setOwnerAddressId(e.target.value)}
                  className="w-full h-9 border rounded px-2 text-sm bg-white"
                  data-testid="owner-address-select"
                >
                  {ownerAddresses.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label || "Anschrift"} — {a.street}
                      {a.house_number ? ` ${a.house_number}` : ""}, {a.zip} {a.city}
                      {a.is_primary ? " (Standard)" : ""}
                    </option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1">
                  Wird u. a. in der Wohnungsgeberbestätigung als Eigentümer-Adresse verwendet.
                </div>
              </div>
            )}

            {/* iter145 — Kündigungs-Zusatzfelder */}
            {isKuendigung && (
              <div className="bg-rose-50/50 border border-rose-200 rounded-md p-3 space-y-3" data-testid="kuendigung-extra">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-800">
                  Kündigungsdetails
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Vertragsende (TT.MM.JJJJ)</Label>
                    <Input value={kExtra.termin} onChange={(e) => setKExtra({ ...kExtra, termin: e.target.value })}
                      placeholder="z. B. 31.05.2026" data-testid="k-termin" />
                  </div>
                  {selected.id === "kuendigung_eigenbedarf" && (
                    <>
                      <div>
                        <Label className="text-xs">Bedarfsperson (Name)</Label>
                        <Input value={kExtra.eigenbedarf_person} onChange={(e) => setKExtra({ ...kExtra, eigenbedarf_person: e.target.value })}
                          placeholder="z. B. Anna Müller" data-testid="k-eb-person" />
                      </div>
                      <div>
                        <Label className="text-xs">Verwandtschaftsgrad</Label>
                        <Input value={kExtra.eigenbedarf_verwandt} onChange={(e) => setKExtra({ ...kExtra, eigenbedarf_verwandt: e.target.value })}
                          placeholder="z. B. Tochter des Vermieters" data-testid="k-eb-relation" />
                      </div>
                    </>
                  )}
                  {selected.id === "kuendigung_vertragsverletzung" && (
                    <div>
                      <Label className="text-xs">Datum Abmahnung</Label>
                      <Input type="date" value={kExtra.abmahnung_datum} onChange={(e) => setKExtra({ ...kExtra, abmahnung_datum: e.target.value })}
                        data-testid="k-abmahnung" />
                    </div>
                  )}
                  {selected.id === "kuendigung_zahlungsrueckstand" && (
                    <>
                      <div>
                        <Label className="text-xs">Rückstand (EUR)</Label>
                        <Input value={kExtra.rueckstand_eur} onChange={(e) => setKExtra({ ...kExtra, rueckstand_eur: e.target.value })}
                          placeholder="z. B. 1.850,00 €" data-testid="k-rueckstand-eur" />
                      </div>
                      <div>
                        <Label className="text-xs">Rückstands-Zeitraum</Label>
                        <Input value={kExtra.rueckstand_periode} onChange={(e) => setKExtra({ ...kExtra, rueckstand_periode: e.target.value })}
                          placeholder="z. B. Januar und Februar 2026" data-testid="k-rueckstand-periode" />
                      </div>
                    </>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Begründung / Sachverhalt</Label>
                  <textarea
                    className="mt-1 w-full min-h-[70px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    value={kExtra.grund} onChange={(e) => setKExtra({ ...kExtra, grund: e.target.value })}
                    placeholder="Freitext — konkrete Begründung, Sachverhalt, weitere Details"
                    data-testid="k-grund"
                  />
                </div>
                <div className="text-[11px] text-rose-900/80 italic">
                  Bei „Zahlungsrückstand“ wird der offene Rückstand automatisch aus dem Mietkonto (open_by_period) übernommen.
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={rawFormOnly} onCheckedChange={(v) => setRawFormOnly(!!v)} data-testid="official-raw-only" />
                <span>
                  <b>Reines Formular</b> — ohne Geschäftspapier / Briefkopf ausgeben (z. B. für die Meldebehörde als Amtsformular).
                  <span className="text-[11px] text-slate-500"> Anschreiben wird bei reinen Formularen deaktiviert.</span>
                </span>
              </label>
              <label className={`flex items-center gap-2 text-sm cursor-pointer ${rawFormOnly ? "opacity-50 pointer-events-none" : ""}`}>
                <Checkbox checked={includeCover} onCheckedChange={(v) => setIncludeCover(!!v)} data-testid="official-cover" />
                <span>Anschreiben (Cover-Letter auf Geschäftspapier) vor das Formular legen</span>
              </label>
              {/* iter167 · Anschreiben inline editierbar */}
              {includeCover && !rawFormOnly && (
                <div className="flex flex-wrap items-center gap-2 pl-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openCoverEditor}
                    className="h-7 text-xs"
                    data-testid="cover-edit-open"
                  >
                    <FileText size={12} className="mr-1.5" />
                    Anschreiben bearbeiten
                  </Button>
                  {coverEdited && (
                    <span className="text-[11px] text-teal-700 italic">
                      · individueller Text wird verwendet
                    </span>
                  )}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={saveToDocs} onCheckedChange={(v) => setSaveToDocs(!!v)} data-testid="official-save" />
                <span>In die Dokumentenmappe dieses Mietverhältnisses ablegen</span>
              </label>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800 flex items-start gap-2">
              <Warning size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                Einige Werte in den Vorlagen sind mit <em>_______</em> markiert und müssen im geöffneten PDF/Word manuell ergänzt werden
                (z. B. bei Mieterhöhung: neuer Betrag, Datum, Begründung). Das System befüllt automatisch nur Angaben, die aus Ihren Stammdaten stammen.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button variant="outline" onClick={() => generate("preview")} disabled={busy} data-testid="official-preview-btn">
                <Eye size={14} className="mr-1.5" /> Vorschau öffnen
              </Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={() => setSendOpen(true)}
                disabled={busy || sendBusy}
                data-testid="official-direct-send-btn"
              >
                <PaperPlaneTilt size={14} className="mr-1.5" />
                Direkt-Versand
              </Button>
              {onAttachToEditor && (
                <Button
                  variant="outline"
                  onClick={() => {
                    // 2026-09 · „Als Anlage" liefert IMMER nur das reine Formular
                    // (kein Anschreiben — der Editor schreibt selbst).
                    setRawFormOnly(true);
                    setIncludeCover(false);
                    setTimeout(() => generate("attach"), 30);
                  }}
                  disabled={busy}
                  data-testid="official-attach-editor-btn"
                >
                  <FileText size={14} className="mr-1.5" /> Als Anlage in Editor
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => generate("download")}
                disabled={busy}
                className="text-slate-500 hover:text-slate-900 ml-auto"
                data-testid="official-generate-btn"
                title="Nur PDF herunterladen (kein Versand)"
              >
                <Download size={14} className="mr-1.5" /> Nur PDF (Download)
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* 2026-09 · Direkt-Versand Modal */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md" data-testid="direct-send-dialog">
          <DialogHeader>
            <DialogTitle>Direkt-Versand</DialogTitle>
            <DialogDescription>
              Wie soll {selected?.name || "das Dokument"} versendet werden? Das Anschreiben wird
              automatisch erzeugt und dem Formular beigefügt.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button
              variant="outline"
              className="justify-start h-auto py-3 gap-3"
              onClick={() => directSend("email")}
              disabled={sendBusy}
              data-testid="direct-send-email"
            >
              <Envelope size={18} className="text-teal-600" />
              <div className="text-left">
                <div className="font-semibold text-sm">Per E-Mail versenden</div>
                <div className="text-xs text-slate-500">
                  Anschreiben wird zum E-Mail-Text · Formular als PDF-Anhang
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3 gap-3"
              onClick={() => directSend("post")}
              disabled={sendBusy}
              data-testid="direct-send-post"
            >
              <Printer size={18} className="text-teal-600" />
              <div className="text-left">
                <div className="font-semibold text-sm">Per Post · Druckansicht</div>
                <div className="text-xs text-slate-500">
                  Anschreiben + Formular werden zu einer druckbaren PDF verschmolzen
                </div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="justify-start h-auto py-3 gap-3"
              onClick={() => directSend("fax")}
              disabled={sendBusy}
              data-testid="direct-send-fax"
            >
              <Printer size={18} className="text-teal-600 opacity-60" />
              <div className="text-left">
                <div className="font-semibold text-sm">Per Fax versenden</div>
                <div className="text-xs text-slate-500">
                  Anschreiben + Formular als kombinierte Fax-PDF (Provider derzeit deaktiviert)
                </div>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendOpen(false)}>Abbrechen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter167 · Anschreiben-Editor Modal */}
      <Dialog open={editCoverOpen} onOpenChange={setEditCoverOpen}>
        <DialogContent className="max-w-2xl" data-testid="cover-edit-dialog">
          <DialogHeader>
            <DialogTitle>Anschreiben bearbeiten</DialogTitle>
            <DialogDescription>
              Der Text wird als Anschreiben auf Ihrem Geschäftspapier gedruckt.
              Absätze durch Leerzeile trennen.
            </DialogDescription>
          </DialogHeader>
          {coverBodyLoading ? (
            <div className="text-sm text-slate-500 py-6 text-center">Anschreiben wird geladen…</div>
          ) : (
            <textarea
              className="w-full min-h-[260px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-mono leading-6"
              value={coverBody}
              onChange={(e) => { setCoverBody(e.target.value); setCoverEdited(true); }}
              data-testid="cover-edit-textarea"
            />
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                setCoverBody(""); setCoverEdited(false);
                setCoverBodyLoading(true);
                try {
                  const { data } = await api.post("/official-documents/cover-text", {
                    template_id: selected.id,
                    tenancy_id: tenancyId,
                    extra_context: isKuendigung ? { kuendigung: kExtra } : undefined,
                  });
                  setCoverBody(data?.text || "");
                } finally { setCoverBodyLoading(false); }
              }}
              data-testid="cover-edit-reset"
            >
              Auf Standard zurücksetzen
            </Button>
            <Button variant="outline" onClick={previewCover} disabled={busy} data-testid="cover-edit-preview">
              <Eye size={14} className="mr-1.5" /> Vorschau
            </Button>
            <Button
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => { setEditCoverOpen(false); toast.success("Anschreiben übernommen"); }}
              data-testid="cover-edit-save"
            >
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
